import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { hashItemKey } from "@/lib/credentials";

export type AssessmentItemType = "action" | "gap";

export interface ProgressRow {
  id: string;
  item_type: AssessmentItemType;
  item_key: string;
  status: "done";
  note: string | null;
}

export const useAssessmentProgress = (companyProfileId: string | undefined) => {
  const { user } = useAuth();
  const [items, setItems] = useState<ProgressRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || !companyProfileId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("company_assessment_progress")
      .select("id, item_type, item_key, status, note")
      .eq("company_profile_id", companyProfileId);
    if (!error) setItems((data ?? []) as ProgressRow[]);
    setLoading(false);
  }, [user, companyProfileId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const doneSet = useMemo(() => new Set(items.map((i) => `${i.item_type}|${i.item_key}`)), [items]);
  const isDone = useCallback(
    (type: AssessmentItemType, key: string) => doneSet.has(`${type}|${key}`),
    [doneSet],
  );
  const getRow = useCallback(
    (type: AssessmentItemType, key: string) =>
      items.find((i) => i.item_type === type && i.item_key === key) ?? null,
    [items],
  );

  const toggleDone = useCallback(
    async (type: AssessmentItemType, title: string) => {
      if (!user || !companyProfileId) {
        toast.error("Sign in to track progress");
        return null;
      }
      const key = await hashItemKey(type, title);
      const existing = getRow(type, key);
      if (existing) {
        const { error } = await supabase
          .from("company_assessment_progress")
          .delete()
          .eq("id", existing.id);
        if (error) {
          toast.error("Could not update progress");
          return null;
        }
        await refresh();
        return { key, action: "removed" as const };
      }
      const { error } = await supabase.from("company_assessment_progress").insert({
        user_id: user.id,
        company_profile_id: companyProfileId,
        item_type: type,
        item_key: key,
        status: "done",
      });
      if (error) {
        toast.error("Could not mark done");
        return null;
      }
      await refresh();
      return { key, action: "added" as const };
    },
    [user, companyProfileId, getRow, refresh],
  );

  const setNote = useCallback(
    async (type: AssessmentItemType, title: string, note: string) => {
      if (!user || !companyProfileId) return false;
      const key = await hashItemKey(type, title);
      const existing = getRow(type, key);
      if (!existing) return false;
      const { error } = await supabase
        .from("company_assessment_progress")
        .update({ note: note.trim() || null })
        .eq("id", existing.id);
      if (error) {
        toast.error("Could not save note");
        return false;
      }
      await refresh();
      return true;
    },
    [user, companyProfileId, getRow, refresh],
  );

  return { items, loading, isDone, getRow, toggleDone, setNote, refresh };
};